import { useQuery, useQueryClient } from 'react-query';

import { useCompanyId } from 'modules/app/hooks/useCompanyId';
import { getV2ApiCompanyIdTravelEntityTypeEntityIdTrip } from 'src/client/spendesk-api';
import { type GetV2ApiCompanyIdTravelEntityTypeEntityIdTripResponseOK } from 'src/client/spendesk-api-types';

export type EntityTrip = Pick<
  GetV2ApiCompanyIdTravelEntityTypeEntityIdTripResponseOK['data']['trip'],
  // Add additional properties as needed
  | 'id'
  | 'name'
  | 'companyId'
  | 'userId'
  | 'cardId'
  | 'status'
  | 'startDate'
  | 'endDate'
  | 'route'
  | 'budget'
  | 'active'
  | 'creation'
  | 'deletion'
>;

const queryKeyFunction = (
  entityType: 'requests' | 'payments' | 'payables',
  entityId: string,
) => [entityType, entityId, 'trip'];

export const useQueryEntityTrip = ({
  isEnabled = true,
  entityType,
  entityId,
}: {
  entityType: 'requests' | 'payments' | 'payables';
  entityId: string;
  isEnabled: boolean;
}) => {
  const companyId = useCompanyId();
  return useQuery(
    queryKeyFunction(entityType, entityId),
    async () => {
      const response = await getV2ApiCompanyIdTravelEntityTypeEntityIdTrip({
        path: { companyId, entityType, entityId },
        query: { enrichBudgets: 'true' },
      });
      if (response.statusCode === 200) {
        return response.body?.data as {
          trip: EntityTrip;
        };
      }
      if (response.statusCode === 204) {
        return { trip: undefined };
      }
      throw new Error('Failed to fetch entity trip');
    },
    {
      retry: false,
      refetchOnWindowFocus: false,
      enabled: isEnabled,
    },
  );
};

export const useUpdateEntityTripCache = () => {
  const queryClient = useQueryClient();

  return (
    entityType: 'requests' | 'payments' | 'payables',
    entityId: string,
    trip?: EntityTrip,
  ) => {
    queryClient.setQueryData<{ trip: EntityTrip } | undefined>(
      queryKeyFunction(entityType, entityId),
      () => {
        if (!trip) {
          return { trip: undefined as unknown as EntityTrip };
        }
        return {
          trip,
        };
      },
    );
  };
};

export const useInvalidateEntityTripCache = () => {
  const queryClient = useQueryClient();

  return (
    entityType: 'requests' | 'payments' | 'payables',
    entityId: string,
  ) => {
    queryClient.invalidateQueries(queryKeyFunction(entityType, entityId));
  };
};
